import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from '@siaedts/shared/lib/supabase';
import { FeedbackPanel } from '../components/FeedbackPanel';
import { compareConfig, useCompareProducts } from '../context/CompareContext';

interface Product {
  id: string;
  name: string;
  slug: string;
  category: string;
  price: number;
  currency?: string;
  description: string;
  longDescription: string;
  image?: string;
  galleryImages: string[];
  featured: boolean;
  specifications: Record<string, string>;
  features: string[];
  includes: string[];
}

type ProductDetailTab = 'features' | 'specifications' | 'included';

export const mockProducts: Record<string, Product> = {
  'john-deere-6155r': {
    id: '1',
    name: 'John Deere 6155R Tractor',
    slug: 'john-deere-6155r',
    category: 'Tractors',
    price: 145000,
    galleryImages: [],
    description: '155 HP premium row-crop tractor with advanced precision farming technology',
    longDescription: 'The John Deere 6155R is a premium row-crop tractor designed for maximum productivity and operator comfort. With 155 horsepower and the advanced AutoQuad Plus transmission, this tractor delivers smooth, efficient power transfer for all your field operations. The CommandView III cab provides industry-leading visibility, comfort, and noise reduction, making long days in the field more manageable. Integrated precision farming technology including AutoTrac guidance and JDLink connectivity ensures you can optimize every pass across your fields.',
    featured: true,
    specifications: {
      'Engine Power': '155 HP (115 kW)',
      'Rated Speed': '2,100 rpm',
      'Transmission': 'AutoQuad Plus 20F/20R',
      'Hydraulic Flow': '114 L/min (30 gpm)',
      'Hydraulic Remotes': '4 Electronic SCVs',
      '3-Point Hitch': 'Category 3/3N, 9,200 kg lift',
      'PTO': '540/1000 rpm',
      'Wheelbase': '2,765 mm (109 in)',
      'Weight': '7,200 kg (15,873 lb)',
      'Fuel Capacity': '335 L (88.5 gal)',
      'Cab': 'CommandView III with ActiveSeat',
      'Guidance': 'AutoTrac Ready',
      'Connectivity': 'JDLink Enabled',
    },
    features: [
      'AutoQuad Plus transmission for seamless shifting',
      'CommandView III cab with premium comfort',
      'AutoTrac guidance system ready',
      'JDLink telematics for remote monitoring',
      'IVT (Infinitely Variable Transmission) option available',
      'ActiveSeat suspension for operator comfort',
      'LED lighting package standard',
      'Integrated precision ag technology',
    ],
    includes: [
      'Operator manual and safety documentation',
      'Standard tool kit',
      'First service kit (filters, fluids)',
      '2-year/2000-hour comprehensive warranty',
      'JDLink 5-year subscription',
      'Dealer delivery and orientation',
    ],
  },
  'valley-center-pivot': {
    id: '2',
    name: 'Valley Center Pivot System',
    slug: 'valley-center-pivot',
    category: 'Irrigation',
    price: 85000,
    galleryImages: [],
    description: 'Efficient center pivot irrigation covering up to 160 acres with smart controls',
    longDescription: 'The Valley Center Pivot System represents the pinnacle of irrigation technology. Designed to cover up to 160 acres with a single unit, this system features the advanced Valley ICON smart panel for precise water management. The ICON system provides real-time monitoring, remote control via smartphone, and automated scheduling based on weather data and soil moisture sensors. Built with Valley\'s legendary durability, including galvanized steel structure and heavy-duty gearboxes, this system delivers uniform water application for maximum crop yield.',
    featured: true,
    specifications: {
      'Max Coverage': '160 acres (65 hectares)',
      'Span Length': '180 ft (55 m) standard',
      'Number of Spans': 'Up to 8',
      'Control System': 'Valley ICON Smart Panel',
      'Operating Pressure': '35-60 PSI (2.4-4.1 bar)',
      'Flow Rate': 'Up to 1,200 GPM (272 m³/hr)',
      'Drive System': 'Tower drive with planetary gearboxes',
      'Structure': 'Hot-dip galvanized steel',
      'Tires': '11.2-24 6-ply traction tires',
      'Sprinklers': 'Nelson or Senninger options',
      'Remote Access': 'Smartphone app & web portal',
      'Sensors': 'Pressure, flow, weather, soil moisture',
      'Power': 'Electric (3-phase) or diesel generator',
      'Warranty': '5-year structural, 3-year components',
    },
    features: [
      'Valley ICON smart control panel',
      'Remote monitoring and control via app',
      'Automated scheduling with weather integration',
      'Real-time alerts and notifications',
      'Variable rate irrigation (VRI) ready',
      'Corner arm option for maximum coverage',
      'Chemigation/fertigation capable',
      'Heavy-duty planetary gearboxes',
      'Hot-dip galvanized for corrosion resistance',
      'Low-pressure sprinkler options for water savings',
    ],
    includes: [
      'Complete pivot structure (spans, towers, drives)',
      'Valley ICON control panel with cellular modem',
      'Sprinkler package (design-specific)',
      'Installation hardware and fasteners',
      'Startup and commissioning service',
      'Operator training session',
      '5-year structural warranty',
      '3-year components warranty',
      'First-year cellular data plan',
    ],
  },
  'case-ih-axial-flow-250': {
    id: '3',
    name: 'Case IH Axial-Flow 250',
    slug: 'case-ih-axial-flow-250',
    category: 'Harvesting',
    price: 320000,
    galleryImages: [],
    description: 'High-capacity combine harvester with advanced grain quality monitoring',
    longDescription: 'The Case IH Axial-Flow 250 series sets a new standard for harvesting performance. With up to 450 HP and the industry-leading single rotor design, these combines deliver exceptional grain quality and throughput in all conditions. The AFS Harvest Command automation system continuously optimizes rotor speed, concave clearance, and cleaning system settings for maximum efficiency. The luxury cab with ActiveView camera system and leather-appointed interior makes harvest season comfortable and productive.',
    featured: true,
    specifications: {
      'Engine Power': '450 HP (336 kW) max',
      'Rotor Diameter': '30 in (762 mm)',
      'Rotor Length': '146 in (3,708 mm)',
      'Grain Tank Capacity': '410 bu (14.4 m³)',
      'Unload Rate': '4.5 bu/sec (158 L/sec)',
      'Unload Auger Length': '26 ft (7.9 m)',
      'Cleaning System': 'Self-leveling, 5,400 sq in',
      'Chopper': 'Redekop MAV 2.0 standard',
      'Header Compatibility': 'Up to 45 ft draper, 12-row corn',
      'Cab': 'Luxury with ActiveView cameras',
      'Automation': 'AFS Harvest Command',
      'Connectivity': 'AFS Connect telematics',
      'Fuel Capacity': '300 gal (1,135 L)',
      'Weight': '38,500 lb (17,463 kg) base',
    },
    features: [
      'Single rotor design for gentle grain handling',
      'AFS Harvest Command automation',
      'ActiveView 360-degree camera system',
      'Luxury cab with leather and climate control',
      'Redekop MAV 2.0 residue management',
      'Self-leveling cleaning system',
      'High-capacity grain tank with fast unload',
      'AFS Connect for fleet management',
      'Compatible with all major header types',
      'Advanced grain quality monitoring',
    ],
    includes: [
      'Combine harvester with selected configuration',
      'AFS Harvest Command activation',
      'AFS Connect 5-year subscription',
      'Redekop MAV 2.0 chopper',
      'Standard header adapter',
      'Operator manuals and safety kit',
      'First service kit',
      '2-year/1000-hour comprehensive warranty',
      'Dealer delivery, setup, and training',
    ],
  },
};

interface RemoteProduct {
  id: string;
  name: string;
  slug: string;
  category_id?: string;
  status?: string;
  short_description?: string | null;
  description?: string | null;
  image_url?: string | null;
  price?: number | null;
  currency?: string;
  gallery_images?: string[];
  features?: string[] | null;
  specifications?: Record<string, string> | null;
  included_items?: string[] | null;
  featured: boolean;
  category?: { name: string; image_url?: string | null } | null;
}

function formatProductPrice(price: number, currency = 'USD') {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(price);
}

function mapRemoteProduct(product: RemoteProduct): Product {
  return {
    id: product.id,
    name: product.name,
    slug: product.slug,
    category: product.category?.name ?? 'Accessories',
    price: product.price ?? 0,
    currency: product.currency ?? 'USD',
    description: product.short_description ?? product.description ?? '',
    longDescription: product.description ?? product.short_description ?? '',
    image: product.image_url ?? product.gallery_images?.[0] ?? product.category?.image_url ?? undefined,
    galleryImages: product.gallery_images ?? [],
    featured: product.featured,
    specifications: product.specifications ?? {},
    features: product.features ?? [],
    includes: product.included_items ?? [],
  };
}

export function ProductDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { items, addItem, removeItem, isInCompare, canAddMore } = useCompareProducts();
  const [product, setProduct] = useState<Product | null>(null);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [activeTab, setActiveTab] = useState<ProductDetailTab>('features');
  const [selectedImage, setSelectedImage] = useState<string | undefined>();
  const [loading, setLoading] = useState(Boolean(slug));

  useEffect(() => {
    let active = true;
    if (!slug) {
      setLoading(false);
      return;
    }

    void db.products.getBySlug(slug)
      .then(async (remoteProduct) => {
        if (!active) return;
        const productData = remoteProduct as unknown as RemoteProduct | null;
        if (!productData || productData.status !== 'available') {
          setProduct(null);
          return;
        }
        setProduct(mapRemoteProduct(productData));
        const remoteRelatedProducts = await db.products.getAll({ status: 'available', limit: 24 });
        setRelatedProducts(
          (remoteRelatedProducts as unknown as RemoteProduct[])
            .filter((relatedProduct) => relatedProduct.id !== productData.id)
            .sort((left, right) => {
              const leftSameCategory = left.category_id === productData.category_id ? 0 : 1;
              const rightSameCategory = right.category_id === productData.category_id ? 0 : 1;
              return leftSameCategory - rightSameCategory;
            })
            .map(mapRemoteProduct),
        );
      })
      .catch(() => {
        if (active) {
          setProduct(null);
          setRelatedProducts([]);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [slug]);

  useEffect(() => {
    setActiveTab('features');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  useEffect(() => {
    setSelectedImage(product?.image);
  }, [product]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50"><p className="text-gray-600">Loading product...</p></div>;
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h1>
          <p className="text-gray-600 mb-6">The product you're looking for doesn't exist.</p>
          <Link to="/products" className="btn-primary">Back to Products</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Breadcrumb */}
      <nav className="bg-gray-50 border-b border-gray-200 py-4" aria-label="Breadcrumb">
        <div className="container-main">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/" className="text-gray-500 hover:text-green-700">Home</Link>
            </li>
            <li className="text-gray-400" aria-hidden="true">/</li>
            <li>
              <Link to="/products" className="text-gray-500 hover:text-green-700">Products</Link>
            </li>
            <li className="text-gray-400" aria-hidden="true">/</li>
            <li>
              <Link to={`/products?category=${product.category}`} className="text-gray-500 hover:text-green-700">
                {product.category}
              </Link>
            </li>
            <li className="text-gray-400" aria-hidden="true">/</li>
            <li className="text-gray-900 font-medium truncate max-w-xs" aria-current="page">
              {product.name}
            </li>
          </ol>
        </div>
      </nav>

      {/* Product Detail */}
      <section className="section bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Image Gallery */}
            <div className="space-y-4">
              <div className="aspect-video bg-gray-100 rounded-xl overflow-hidden relative">
                {selectedImage ? <img src={selectedImage} alt={product.name} className="absolute inset-0 h-full w-full object-cover" /> : <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                  <svg className="w-24 h-24" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>}
                {product.featured && (
                  <div className="absolute top-4 right-4">
                    <span className="badge badge-green">Featured</span>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-4 gap-4">
                {[product.image, ...product.galleryImages].filter(Boolean).map((image, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setSelectedImage(image)}
                    className={`aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 transition-colors ${selectedImage === image ? 'border-green-600' : 'border-transparent hover:border-green-300'}`}
                    aria-pressed={selectedImage === image}
                    aria-label={`View image ${i}`}
                  >
                    {image ? <img src={image} alt={`${product.name} detail ${i + 1}`} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>}
                  </button>
                ))}
              </div>
            </div>

            {/* Product Info */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <span className="badge badge-blue">{product.category}</span>
                {product.featured && <span className="badge badge-green">Featured</span>}
              </div>
              
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">{product.name}</h1>
              
              <div className="flex items-baseline space-x-4 mb-6">
                <span className="text-3xl font-bold text-green-700">{formatProductPrice(product.price, product.currency)}</span>
                <span className="text-gray-500">{product.currency ?? 'USD'}</span>
              </div>

              <p className="text-gray-600 text-lg mb-8">{product.description}</p>

              {/* Quick Actions */}
              <div className="flex flex-wrap gap-4 mb-8 pb-8 border-b border-gray-200">
                <Link
                  to={`/contact?subject=${encodeURIComponent('Pricing & Quotes')}&productId=${encodeURIComponent(product.id)}&productName=${encodeURIComponent(product.name)}`}
                  className="btn-primary flex-1 sm:flex-none text-center"
                >
                  Request Quote
                </Link>
                <Link
                  to={`/contact?subject=${encodeURIComponent('Product Information')}&productId=${encodeURIComponent(product.id)}&productName=${encodeURIComponent(product.name)}`}
                  className="btn-outline flex-1 sm:flex-none text-center"
                >
                  Contact Sales
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    if (!product) return;
                    if (isInCompare(product.id)) {
                      removeItem(product.id);
                      return;
                    }
                    if (!canAddMore) {
                      return;
                    }
                    addItem({
                      id: product.id,
                      name: product.name,
                      slug: product.slug,
                      category: product.category,
                      price: product.price,
                      currency: product.currency,
                      description: product.description,
                      image: product.image,
                      featured: product.featured,
                      specifications: product.specifications,
                    });
                  }}
                  className={`btn-secondary flex-1 sm:flex-none ${isInCompare(product.id) ? 'bg-green-50 text-green-700 border-green-200' : ''} ${!canAddMore && !isInCompare(product.id) ? 'opacity-60 cursor-not-allowed' : ''}`}
                  disabled={!canAddMore && !isInCompare(product.id)}
                  aria-label={isInCompare(product.id) ? `Remove ${product.name} from quote list` : `Add ${product.name} to quote list`}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  {isInCompare(product.id) ? 'Remove from Quote List' : canAddMore ? 'Add to Quote List' : `Quote list full (${compareConfig.maxItems})`}
                </button>
                {items.length > 0 && (
                  <Link to="/quote-list" className="btn-outline flex-1 sm:flex-none text-center">
                    View quote list ({items.length})
                  </Link>
                )}
              </div>

              {/* Key Specifications */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                {Object.entries(product.specifications).slice(0, 8).map(([key, value]) => (
                  <div key={key} className="bg-gray-50 rounded-lg p-4">
                    <dt className="text-sm text-gray-500 mb-1">{key}</dt>
                    <dd className="font-medium text-gray-900">{value}</dd>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Detailed Description */}
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Product Overview</h2>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-600 leading-relaxed">{product.longDescription}</p>
            </div>
          </div>

          <FeedbackPanel contentType="product" contentId={product.id} title="What do you think about this product?" />

          {/* Features & Specifications Tabs */}
          <div className="mt-12">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8" aria-label="Product details tabs">
                <button
                  type="button"
                  id="product-tab-features"
                  role="tab"
                  aria-selected={activeTab === 'features'}
                  aria-controls="product-panel-features"
                  onClick={() => setActiveTab('features')}
                  className={`${activeTab === 'features' ? 'tab-active text-green-700 border-green-700' : 'tab-inactive text-gray-500 border-transparent hover:text-gray-700'} pb-4 text-sm font-medium border-b-2`}
                >
                  Key Features
                </button>
                <button
                  type="button"
                  id="product-tab-specifications"
                  role="tab"
                  aria-selected={activeTab === 'specifications'}
                  aria-controls="product-panel-specifications"
                  onClick={() => setActiveTab('specifications')}
                  className={`${activeTab === 'specifications' ? 'tab-active text-green-700 border-green-700' : 'tab-inactive text-gray-500 border-transparent hover:text-gray-700'} pb-4 text-sm font-medium border-b-2`}
                >
                  Full Specifications
                </button>
                <button
                  type="button"
                  id="product-tab-included"
                  role="tab"
                  aria-selected={activeTab === 'included'}
                  aria-controls="product-panel-included"
                  onClick={() => setActiveTab('included')}
                  className={`${activeTab === 'included' ? 'tab-active text-green-700 border-green-700' : 'tab-inactive text-gray-500 border-transparent hover:text-gray-700'} pb-4 text-sm font-medium border-b-2`}
                >
                  What's Included
                </button>
              </nav>
            </div>

            <div className="mt-6">
              {/* Features */}
              <div id="product-panel-features" role="tabpanel" aria-labelledby="product-tab-features" hidden={activeTab !== 'features'}>
                {product.features.length > 0 ? <ul className="grid grid-cols-1 md:grid-cols-2 gap-4" role="list">
                  {product.features.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                      <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul> : <p className="text-gray-600">Detailed features will be available soon.</p>}
              </div>

              {/* Full Specifications */}
              <div id="product-panel-specifications" role="tabpanel" aria-labelledby="product-tab-specifications" hidden={activeTab !== 'specifications'}>
                {Object.keys(product.specifications).length > 0 ? <dl className="divide-y divide-gray-200">
                  {Object.entries(product.specifications).map(([key, value]) => (
                    <div key={key} className="py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                      <dt className="text-gray-600 font-medium mb-2 sm:mb-0 sm:w-1/2">{key}</dt>
                      <dd className="text-gray-900 sm:w-1/2 text-right sm:text-left">{value}</dd>
                    </div>
                  ))}
                </dl> : <p className="text-gray-600">Detailed specifications will be available soon.</p>}
              </div>

              {/* What's Included */}
              <div id="product-panel-included" role="tabpanel" aria-labelledby="product-tab-included" hidden={activeTab !== 'included'}>
                {product.includes.length > 0 ? <ul className="space-y-3" role="list">
                  {product.includes.map((item, index) => (
                    <li key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul> : <p className="text-gray-600">Included items will be confirmed with your quote.</p>}
              </div>
            </div>
          </div>

          {/* Related Products */}
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">You May Also Like</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedProducts.slice(0, 3).map((related) => (
                  <article key={related.id} className="card group">
                      <Link to={`/products/${related.slug}`} className="block" aria-label={`View ${related.name} details`}>
                      <div className="aspect-video bg-gray-100 relative overflow-hidden">
                      {related.image ? <img src={related.image} alt={related.name} className="absolute inset-0 h-full w-full object-cover" /> : <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-gray-400">
                        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg><span className="text-xs">Image coming soon</span>
                      </div>}
                      <div className="absolute top-3 left-3">
                        <span className="badge badge-blue">{related.category}</span>
                      </div>
                      </div>
                    </Link>
                      <div className="p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-green-700 transition-colors">
                          <Link to={`/products/${related.slug}`}>{related.name}</Link>
                        </h3>
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">{related.description}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-xl font-bold text-green-700">{formatProductPrice(related.price, related.currency)}</span>
                          <Link to={`/products/${related.slug}`} className="text-sm text-green-700 font-medium hover:underline">
                            View Details →
                          </Link>
                        </div>
                      </div>
                  </article>
                ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}